import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetTrackPODetailsMutation,
  useUpdateDiscountAndPriceOrderMutation,
  useEditOrderMutation,
  useCancelOrderMutation,
  OrderLine,
  LineDiscount,
  dispatch,
  getWorkspaceId,
  useAppSelector,
  useCheckoutOrdersMutation,
  updateProductLineItems,
  OrderDataLine,
  updateExtraDetails,
  ProductCardVariant,
  updateOrderStatus,
  useNotifyToBuyerMutation,
  getOrderPricelist,
  showToast,
  ErrorVariants,
  updateOrderDetailsData,
  useAcceptOrderMutation,
  getCatalogConfig,
  useGetOrderDetailsMutation,
  OrderDetailsResponse,
  AddLineItems,
  getAppType,
  APP_TYPE,
  useRemoveOrderPromotionMutation,
  OrderLevelScheme,
  useGetOrderLevelSchemesMutation,
  GetOrderLevelSchemesResponse,
  usePublishMessageMutation,
  getChannel,
  useLazyGetChannelDetailsQuery,
  updateChannel,
  getUserData,
  getToken,
} from '@zonofi/zono-money-store';
import {
  OrderDiscountLevel,
  OrderProductUpdateType,
  DiscountType,
  OrderProductActionType,
  OrderStatusEnum,
  SortDirection,
  SELLER_WEBAPP_ROUTES,
  FIELD_OPS_ROUTES,
  MessageType,
} from '../../constants';
import { processBasicApiResponse, processOrderPatchResponse } from '../helper';
import {
  toMajorCurrency,
  toMinorCurrency,
} from '@zonofi/common/functions/format-amount';
import { orderHasOutOfStockLine } from '../../helper/is-order-line-out-of-stock';
import { useOrderNote } from '../order-note';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { isNil } from 'lodash';

export const useOrderDetailsApi = ({
  setOpenReplaceProductDrawer,
}: {
  setOpenReplaceProductDrawer?: (val: boolean) => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const customerId = searchParams.get('customerId');
  const { orderId: poFileId } = useParams();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const showOutOfStockTag = useAppSelector(getCatalogConfig)?.showOutOfStockTag;
  const apptype = useAppSelector(getAppType);
  const selectedPricelist = useAppSelector(getOrderPricelist);
  const currentChannel = useAppSelector(getChannel);
  const user = useAppSelector(getUserData);
  const token = useAppSelector(getToken);

  const [fetchOrder, orderResponse] = useGetOrderDetailsMutation();
  const [fetchPo, poDetailsResponse] = useGetTrackPODetailsMutation();
  const [updatePriceAndDiscountApi, updatePriceAndDiscountResponse] =
    useUpdateDiscountAndPriceOrderMutation();
  const [editRemoveOrderApi, editRemoveOrderResponse] = useEditOrderMutation();
  const [addDiscountApi, addDiscountApiResponse] = useEditOrderMutation();
  const [cancelOrderApi, cancelOrderRes] = useCancelOrderMutation();
  const [acceptOrderApi, acceptOrderApiResponse] = useAcceptOrderMutation();
  const [checkoutApi, checkoutApiResponse] = useCheckoutOrdersMutation();
  const [notifyBuyerApi, notifyBuyerApiResponse] = useNotifyToBuyerMutation();
  const [editOrder, editOrderResponse] = useEditOrderMutation();
  const { orderNoteFromEditor: orderNote } = useOrderNote();
  const [getOrderLevelSchemesAPI] = useGetOrderLevelSchemesMutation();
  const [removeOrderPromotion, removeOrderPromotionResponse] =
    useRemoveOrderPromotionMutation();
  const [channelDetailsApi] = useLazyGetChannelDetailsQuery();
  const [sendPaymentRequest, sendPaymentRequestResponse] =
    usePublishMessageMutation();

  const [promotionSchemes, setPromotionSchemes] = useState<OrderLevelScheme[]>(
    []
  );
  const [selectedBenefit, setSelectedBenefit] = useState<OrderLevelScheme>();
  const [showPromotionsModal, setShowPromotionsModal] = useState(false);
  const isChangingBenefitRef = useRef(false);
  const [lineActionType, setLineActionType] =
    useState<OrderProductActionType>();
  const [orderDetailsData, setOrderDetailsData] =
    useState<OrderDetailsResponse>();

  const [showToastSuccess, setShowToastSuccess] = useState({
    show: false,
    msg: '',
  });
  const [removeId, setRemoveId] = useState<number | null>();
  const [showToastError, setShowToastError] = useState({
    show: false,
    msg: '',
  });
  const [advanceRemoveLoading, setAdvanceRemoveLoading] = useState(false);

  const hasBlockingOutOfStock = useMemo(
    () => orderHasOutOfStockLine(orderDetailsData?.lines, !!showOutOfStockTag),
    [orderDetailsData?.lines, showOutOfStockTag]
  );

  const orderIds = useMemo(() => {
    const ids = orderDetailsData?.lines?.map((x: any) => x?.orderId) || [];

    const uniqueOrderIds: any[] = [
      ...new Set(ids.filter((id: number | null) => id !== null)),
    ];
    return uniqueOrderIds;
  }, [orderDetailsData]);

  useEffect(() => {
    if (customerId) {
      const payload = {
        workspaceId: sellerWorkspaceId,
        sellerWorkspaceId: sellerWorkspaceId,
        customerId: customerId,
        channelType: 'C',
      };
      channelDetailsApi(payload)?.then((res) => {
        if (res?.data) {
          dispatch(updateChannel(res.data));
        }
      });
    }
  }, [customerId]);

  const orderDetailsApiCall = () => {
    if (!orderId) return;

    fetchOrder({
      workspaceId: sellerWorkspaceId,
      orderId: Number(orderId),
      body: {
        includeInvoice: true,
        includeTax: true,
        includeCustomer: true,
        includePromotions: true,
        customerId: customerId ?? '',
        includePromotionData: false,
      },
    });
  };

  const poDetailsApiCall = () => {
    if (!poFileId) return;

    fetchPo({
      workspaceId: sellerWorkspaceId,
      body: {
        pofileId: poFileId as string,
        includeInvoice: true,
        includePromotion: true,
        includeTax: true,
        sortBy: 'sequence',
        sortDirection: SortDirection.ASCENDING,
        includePromotionData: true,
      },
      orderId: (orderId as string) ?? undefined,
    });
  };

  const openPromotionsModal = () => {
    setShowPromotionsModal(true);
  };

  const fetchDiscounts = async () => {
    if (orderIds?.length === 0) return;
    try {
      // call all apis in parallel
      const responses = await Promise.all(
        orderIds.map(
          (id) =>
            id &&
            getOrderLevelSchemesAPI({
              sellerWorkspaceId: sellerWorkspaceId ?? '',
              orderId: Number(id),
              customerId: customerId ?? '',
            }).unwrap()
        )
      );

      const mergedPromotions = Array.from(
        new Map(
          responses
            .flatMap(
              (response: GetOrderLevelSchemesResponse) =>
                response?.promotions || []
            )
            .map((scheme) => [scheme.id, scheme])
        ).values()
      );

      const schemes: OrderLevelScheme[] = mergedPromotions.map(
        (scheme: any) => {
          const promotionCode = scheme?.promotionCode || scheme?.code || '';
          const minOrderQuantity =
            scheme?.minQtyASC ??
            scheme?.minOrderQuantity ??
            scheme?.slab?.[0]?.from;

          return {
            id: scheme?.id,
            name: scheme?.name || 'Promotion',
            code: promotionCode,
            description: scheme?.description || '',
            schemeType:
              scheme?.schemeType || scheme?.promotionType || 'PROMOTION',
            minOrderQuantity,
            schemeIconType: scheme?.schemeIconType,
            productVariantId: scheme?.productVariantId,
          };
        }
      );
      setPromotionSchemes(schemes && schemes.length > 0 ? schemes : []);
    } catch (error) {
      console.log('getOrderLevelSchemesAPI error', error);
      setPromotionSchemes([]);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, [orderDetailsData]);

  useEffect(() => {
    if (orderId) {
      orderDetailsApiCall();
    } else if (poFileId) {
      poDetailsApiCall();
    }
  }, [orderId, poFileId]);

  useEffect(() => {
    if (orderResponse.isSuccess && orderResponse.data) {
      setOrderDetailsData(orderResponse.data);

      const { lines, status } = orderResponse.data;
      dispatch(updateOrderStatus(status as string));

      if (lines?.length > 0) {
        const lineItems = lines.map((item) => ({
          productVariantId: item?.productVariant?.id,
          productMOQ: item?.minOrderQty,
          quantity: item?.quantity as number,
        }));

        dispatch(updateProductLineItems(lineItems as AddLineItems[]));
      }
      dispatch(
        updateOrderDetailsData({
          data: {
            poNumber: orderResponse?.data?.poFile?.poNumber || '',
            paymentTermsAndConditions: orderResponse?.data?.paymentTerm || '',
            termsAndConditions: orderResponse?.data?.deliveryTerms || '',
            address: orderResponse?.data?.shippingAddress?.streetLine1 || '',
            advancePayment:
              orderResponse?.data?.advancePaymentType === 'percentage'
                ? String(Number(orderResponse?.data?.advancePayment))
                : String(
                    toMajorCurrency(Number(orderResponse?.data?.advancePayment))
                  ),
            advancePaymentType: orderResponse?.data?.advancePaymentType || '',
            advancePaymentAmount:
              orderResponse?.data?.advancePaymentAmount || '',
            location:
              orderResponse?.data?.shippingAddress?.latitude ||
              orderResponse?.data?.shippingAddress?.longitude
                ? `${orderResponse?.data?.shippingAddress?.latitude || '-'}, ${
                    orderResponse?.data?.shippingAddress?.longitude || '-'
                  }`
                : '',
            ...(orderResponse?.data?.notes ?? {}),
          },
          includePaymentLink: orderResponse?.data?.includePaymentLink || false,
        })
      );
    }
  }, [orderResponse]);

  useEffect(() => {
    if (!poDetailsResponse.isSuccess || !poDetailsResponse.data || orderId) {
      return;
    }

    const nextOrderId = poDetailsResponse.data.lines
      ?.find((line) => line?.orderId !== undefined && line?.orderId !== null)
      ?.orderId?.toString();
    const nextCustomerId = poDetailsResponse.data.customerId;

    if (!nextOrderId || !nextCustomerId) return;

    const params = new URLSearchParams(searchParams);
    params.set('orderId', nextOrderId);
    params.set('customerId', nextCustomerId);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    );
  }, [poDetailsResponse, orderId, searchParams]);

  const currentOrderId = useMemo(() => {
    const { lines = [] } = orderDetailsData || {};
    const mappedLines = lines.filter(
      ({ orderId }) => orderId !== undefined && orderId !== null
    );
    return mappedLines?.[0]?.orderId?.toString() || '';
  }, [orderDetailsData]);

  const onSkipPromotion = () => {
    setShowPromotionsModal(false);
  };

  const onCancelPromotion = () => {
    setShowPromotionsModal(false);
  };

  useEffect(() => {
    if (!currentOrderId || orderId) return;

    const params = new URLSearchParams(searchParams); // clone
    params.set('orderId', currentOrderId);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    );
  }, [currentOrderId, orderId]);

  const handleSuccess = () => {
    const routeToNavigate =
      apptype === APP_TYPE.FIELDS_OPS ? FIELD_OPS_ROUTES : SELLER_WEBAPP_ROUTES;
    navigate(routeToNavigate.ORDERS_LIST);
  };

  const handleUpdate = (item: OrderLine, type: OrderProductUpdateType) => {
    editRemoveOrderApi({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: orderDetailsData?.customerId ?? '',
        lines: [
          type === OrderProductUpdateType.QUANTITY
            ? {
                quantity: Number(item?.newQnty),
                productVariantId: item?.productVariant?.id,
                discount: Number(item?.lineDiscount?.discount),
                discountType: item?.lineDiscount?.discountType,
              }
            : {
                quantity: Number(item?.quantity),
                productVariantId: item?.productVariant?.id,
                listPrice: toMinorCurrency(item?.newPrice ?? 0),
              },
        ],
        orderId: item.orderId ? item.orderId.toString() : currentOrderId,
        priceListId: selectedPricelist?.pricelistId,
      },
    });
  };

  const onRemovePress = (item: OrderLine) => {
    setLineActionType(OrderProductActionType.REMOVE);
    setRemoveId(item?.id);
    editRemoveOrderApi({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: orderDetailsData?.customerId ?? '',
        lines: [
          {
            quantity: 0,
            productVariantId: item?.productVariant?.id,
            poFileLineId: item?.poFileLineId,
          },
        ],
        orderId: item?.orderId ? item?.orderId.toString() : currentOrderId,
        poFileId: orderDetailsData?.poFile?.id,
      },
    });
  };

  const onNotifyBuyer = () => {
    if (hasBlockingOutOfStock) {
      dispatch(
        showToast({
          message:
            'This order has out-of-stock items. Replace or remove them before continuing.',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
      return;
    }
    notifyBuyerApi({
      sellerWorkspaceId,
      poFileId: String(orderDetailsData?.poFile?.id),
      customerId: String(orderDetailsData?.customerId),
    });
  };

  const onCancelOrder = () => {
    cancelOrderApi({
      workspaceId: sellerWorkspaceId,
      sellerWorkspaceId,
      orderId: Number(currentOrderId),
      customerId: String(orderDetailsData?.customerId),
    });
  };

  const onAcceptOrder = () => {
    if (hasBlockingOutOfStock) {
      dispatch(
        showToast({
          message:
            'This order has out-of-stock items. Replace or remove them before continuing.',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
      return;
    }
    acceptOrderApi({
      workspaceId: sellerWorkspaceId,
      body: [
        {
          orderId: Number(currentOrderId),
          status: OrderStatusEnum.APPROVED,
        },
      ],
    });
  };

  const onLineDiscountRemove = (
    item: LineDiscount,
    quantity: number,
    productVariantId: number
  ) => {
    updatePriceAndDiscountApi({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: orderDetailsData?.customerId ?? '',
        lines: [
          {
            discount: 0,
            discountType: item?.discountType,
            quantity,
            productVariantId,
          },
        ],
        orderId: currentOrderId,
      },
    });
  };

  const onPlaceOrder = () => {
    if (hasBlockingOutOfStock) {
      dispatch(
        showToast({
          message:
            'This order has out-of-stock items. Replace or remove them before continuing.',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
      return;
    }
    checkoutApi({
      sellerWorkspaceId: sellerWorkspaceId,
      customerId: String(orderDetailsData?.customerId),
      poFileIds: [String(orderDetailsData?.poFile?.id)],
    });
  };

  const onApplyDiscount = (
    line: OrderDataLine,
    discountType: DiscountType,
    discount: number,
    orderDiscountType: OrderDiscountLevel
  ) => {
    const customerId = orderDetailsData?.customerId ?? '';
    const { quantity, productVariantId } = line;
    const linePayload = { quantity, productVariantId };

    const body =
      orderDiscountType === 'item'
        ? {
            customerId,
            orderId: currentOrderId,
            lines: [
              {
                ...linePayload,
                discount,
                discountType,
              },
            ],
          }
        : {
            customerId,
            orderId: currentOrderId,
            discount,
            discountType,
            lines: [linePayload],
          };

    addDiscountApi({
      workspaceId: sellerWorkspaceId,
      body,
    });
  };

  useEffect(() => {
    processOrderPatchResponse(updatePriceAndDiscountResponse, () => {
      orderDetailsApiCall();
    });
  }, [updatePriceAndDiscountResponse]);

  useEffect(() => {
    processOrderPatchResponse(editRemoveOrderResponse, () => {
      orderDetailsApiCall();
      if (lineActionType === OrderProductActionType.REMOVE) {
        setRemoveId(null);
        const updatedItems = editRemoveOrderResponse.data?.orderLines?.map(
          (line) => ({
            productVariantId: line.productVariantId as number,
            productMOQ: line.minOrderQty as number,
            quantity: line.quantity as number,
          })
        );
        if (updatedItems) {
          dispatch(updateProductLineItems(updatedItems));
        }
      }
    });
  }, [editRemoveOrderResponse]);

  useEffect(() => {
    processBasicApiResponse(
      cancelOrderRes,
      handleSuccess,
      'Order Cancelled Successfully',
      'Order Cancellation Failed'
    );
  }, [cancelOrderRes]);

  useEffect(() => {
    processBasicApiResponse(
      acceptOrderApiResponse,
      handleSuccess,
      'Order Acknowledged Successfully',
      'Order Acknowledging Failed'
    );
  }, [acceptOrderApiResponse]);

  useEffect(() => {
    processBasicApiResponse(
      checkoutApiResponse,
      handleSuccess,
      'Order Placed Successfully',
      'Order Placing Failed'
    );
  }, [checkoutApiResponse]);

  useEffect(() => {
    processOrderPatchResponse(
      addDiscountApiResponse,
      () => orderDetailsApiCall(),
      'Discount Applied Successfully'
    );
  }, [addDiscountApiResponse]);

  useEffect(() => {
    processOrderPatchResponse(
      sendPaymentRequestResponse,
      () => orderDetailsApiCall(),
      'Payment Request Sent Successfully'
    );
  }, [sendPaymentRequestResponse]);

  const onReplacePress = (lineItem: OrderLine, type: ProductCardVariant) => {
    dispatch(
      updateExtraDetails({
        customerName: orderDetailsData?.customerName ?? 'N/A',
        orderRefCode: orderDetailsData?.refOrderNumber ?? 'N/A',
        customerId: orderDetailsData?.customerId ?? '',
        productVariantId: lineItem?.productVariant?.id,
        variantType: type,
        poFileId: orderDetailsData?.poFile?.id,
        poFileLineId: lineItem?.poFileLineId,
        orderLineId: lineItem?.id,
        orderId: lineItem?.orderId?.toString(),
      })
    );
    setOpenReplaceProductDrawer?.(true);
  };

  const buildNotesPayload = (
    orderNote: {
      properties?: Record<string, unknown>;
    },
    values: Record<string, string | null>
  ) => {
    if (!orderNote?.properties) return undefined;
    const notes: Record<string, string> = {};
    Object.keys(orderNote.properties).forEach((key) => {
      notes[key] = values[key] ?? '';
    });

    return notes;
  };

  const onSaveDetails = useCallback(
    (values: Record<string, string | null>, includePaymentLink: boolean) => {
      const lines = orderDetailsData?.lines?.map((line) => ({
        orderId: line?.orderId,
        productVariantId: line?.productVariant?.id,
        quantity: line?.quantity || 1,
        unitPrice: Number(line?.unitPrice),
        customerId: orderDetailsData?.customerId ?? '',
      }));

      const notes = buildNotesPayload(orderNote, values);
      const [lat, lng] = (values?.location ?? '').split(',');
      const hasAdvancePayment =
        values?.advancePayment != null &&
        values?.advancePayment !== '' &&
        values?.advancePayment !== '0';

      const payload = {
        workspaceId: sellerWorkspaceId!,
        body: {
          customerId: orderDetailsData?.customerId ?? '',
          orderId: Number(currentOrderId),
          lines: lines,
          deliveryTerms: values?.termsAndConditions || '',
          paymentTerm: values?.paymentTermsAndConditions || '',
          advancePayment: hasAdvancePayment
            ? values.advancePaymentType === 'percentage'
              ? Number(values.advancePayment)
              : toMinorCurrency(Number(values.advancePayment))
            : null,
          advancePaymentType: hasAdvancePayment
            ? values.advancePaymentType
            : null,
          includePaymentLink: includePaymentLink,
          poNumber: values?.poNumber || null,
          shippingAddress: {
            latitude: lat ? Number(lat?.trim()) : null,
            longitude: lng ? Number(lng?.trim()) : null,
            streetLine1: values?.address || '',
          },
          ...(orderNote?.properties ? { notes } : {}),
        },
      };

      editRemoveOrderApi(payload)
        .unwrap()
        .then(() => {
          setAdvanceRemoveLoading(false);
          dispatch(
            showToast({
              message: 'Order Details Updated Successfully',
              type: ErrorVariants.SUCCESS,
              canDisplay: true,
              responseStatus: 'success',
            })
          );
        });
    },
    [orderDetailsData]
  );

  const handleRemoveDetails = useCallback(
    (values: Record<string, string>, includePaymentLink: boolean) => {
      setAdvanceRemoveLoading(true);
      const currentValues = {
        ...values,
        advancePayment: null,
        advancePaymentType: null,
      };
      onSaveDetails(
        currentValues as Record<string, string | null>,
        includePaymentLink
      );
    },
    [onSaveDetails]
  );

  const onRequestPayment = () => {
    const { data: orderData } = orderResponse;
    if (!orderData || Object.keys(orderData).length === 0) return;

    const payload = {
      sellerWorkspaceId: currentChannel?.workspaceId ?? '',
      channelId: currentChannel?.id,
      message: {
        type: MessageType.ORDER,
        sId: '',
        _id: '',
        includePaymentLink: orderData?.includePaymentLink ?? false,
        source: 'system',
        sellerDetails: {
          id: currentChannel?.workspaceId ?? '',
          spaceName: user?.firstName,
          isoLanguageCode: 'en',
        },
        user: {
          senderUserId: user?.id,
          languageCode: 'en',
        },
        data: {
          inviteId: currentChannel?.customerId || customerId || '',
          totalWithTax: Number(orderData?.totalWithTax ?? 0),
          shipping: 0,
          shippingWithTax: 0,
          suggestedOrderNumber: null,
          erpOrderNumber: null,
          refOrderNumber: orderData?.refOrderNumber,
          generationType: null,
          campaignId: null,
          orderDate: orderData?.createdAt,
          itemCount: 2,
          status: orderData?.status,
          orderId: orderData?.id,
          principalWorkspaceId: currentChannel?.workspaceId ?? '',
          eventTimestamp: null,
          cfaId: orderData?.fullFillmentLocationId,
          orderSaving: 0,
          orderValue: Number(orderData?.total ?? 0),
          poNumber: orderData?.poFile?.id,
          orderType: 'whatsapp',
          filePaths: [],
          userType: orderData?.userType,
          orderSource: 'whatsapp',
          totalAmount: orderData?.total,
          discount: orderData?.discount,
          netAmount: orderData?.subTotalWithTax,
          advancePayment: orderData?.advancePayment,
          advancePaymentType: orderData?.advancePaymentType,
          advancePaymentAmount: orderData?.advancePaymentAmount,
          tax: orderData?.taxAmount,
          notes: orderData?.notes,
          detailsLink: `invite/${
            currentChannel?.customerId || customerId || ''
          }?token=${token}&poFileId=${orderData?.poFile?.id}`,
          includePaymentLink: false,
          communicationChannelId: currentChannel?.id,
          paymentTerm: orderData?.paymentTerm,
          sellerWorkspaceId: currentChannel?.workspaceId ?? '',
        },
      },
    };

    sendPaymentRequest(payload);
  };

  useEffect(() => {
    if (
      orderDetailsData?.lines?.some(
        (x) => x?.promotionIds && x?.promotionIds?.length > 0
      )
    ) {
      const offer = orderDetailsData?.lines?.find(
        (x) => x?.promotionIds && x?.promotionIds?.length > 0
      );
      if (offer) {
        setSelectedBenefit({
          id: Number(offer?.promotionIds?.[0]),
          name: offer?.promotionNames?.[0] ?? '',
          code: '',
          description: '',
          schemeType: '',
          minOrderQuantity: offer?.minOrderQty,
          productVariantId: offer?.productVariant?.id,
        });
      }
    }
  }, [orderDetailsData]);

  const onApplyPromotion = (
    promotionIds: (string | number)[],
    scheme: OrderLevelScheme
  ) => {
    orderIds?.map(async (orderId) => {
      const selectedProducts = orderDetailsData?.lines?.filter(
        (x) => x?.productVariantId === scheme?.productVariantId
      );
      try {
        await editOrder({
          workspaceId: sellerWorkspaceId,
          body: {
            customerId: customerId ?? orderDetailsData?.customerId ?? '',
            orderId,
            lines:
              selectedProducts?.map((x) => ({
                quantity: x?.quantity ?? 1,
                productVariantId: x?.productVariant?.id,
                ...(typeof x?.listPrice === 'number'
                  ? { listPrice: x?.linePrice }
                  : {}),
              })) ?? [],
            promotionIds,
          },
        }).unwrap();
        setSelectedBenefit(scheme);
      } catch {
        return;
      }
    });
  };

  const applyPromotionOnOrder = async (scheme: OrderLevelScheme) => {
    const selectedLine =
      orderDetailsData?.lines?.find(
        (line) => line?.productVariantId === (scheme as any)?.productVariantId
      ) || orderDetailsData?.lines?.[0];
    if (
      selectedLine?.promotionIds &&
      selectedLine?.promotionIds?.length > 0 &&
      Number(selectedLine?.promotionIds?.[0]) !== Number(scheme?.id) &&
      selectedLine?.orderId
    ) {
      isChangingBenefitRef.current = true;
      try {
        await removeOrderPromotion({
          workspaceId: sellerWorkspaceId,
          orderId: Number(selectedLine?.orderId),
          promotionId: Number(selectedLine?.promotionIds?.[0]),
        }).unwrap();
      } catch {
        isChangingBenefitRef.current = false;
        setShowPromotionsModal(false);
        return;
      }
    }
    onApplyPromotion([String(scheme.id)], scheme);
    setShowPromotionsModal(false);
  };

  useEffect(() => {
    if (showToastSuccess?.show) {
      const timeout = setTimeout(() => {
        setShowToastSuccess({
          show: false,
          msg: '',
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
    return;
  }, [showToastSuccess]);

  useEffect(() => {
    if (removeOrderPromotionResponse?.isSuccess) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        return;
      }
      setSelectedBenefit(undefined);
      orderDetailsApiCall();
      setShowToastSuccess({
        show: true,
        msg: 'Benefit removed successfully.',
      });
    } else if (removeOrderPromotionResponse?.isError) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        setShowToastError({
          show: true,
          msg: 'Unable to change benefit. Please try again.',
        });
        return;
      }
      setShowToastError({
        show: true,
        msg: 'Unable to remove benefit. Please try again.',
      });
    }
  }, [removeOrderPromotionResponse]);

  useEffect(() => {
    if (editOrderResponse.isSuccess) {
      orderDetailsApiCall();
    }
  }, [editOrderResponse]);

  const showRequestPayment = useMemo(() => {
    const {
      advancePaymentType,
      advancePaymentAmount,
      advancePayment,
      userType,
    } = orderResponse?.data ?? {};

    return (
      userType === 'C' &&
      !isNil(advancePaymentType) &&
      (Number(advancePaymentAmount) > 0 || Number(advancePayment) > 0)
    );
  }, [orderResponse?.data]);

  return {
    orderDetailsData,
    setOrderDetailsData,
    handleUpdate,
    onRemovePress,
    onCancelOrder,
    onAcceptOrder,
    onLineDiscountRemove,
    cancelOrderRes,
    acceptOrderApiResponse,
    onPlaceOrder,
    checkoutApiResponse,
    currentOrderId,
    onApplyDiscount,
    onReplacePress,
    isLoading:
      orderResponse?.isLoading || poDetailsResponse?.isLoading || false,
    onNotifyBuyer,
    notifyBuyerApiResponse,
    triggerRefetch: orderDetailsApiCall,
    onSaveDetails,
    savingDetailsLoader: editRemoveOrderResponse.isLoading,
    selectedBenefit,
    setSelectedBenefit,
    showPromotionsModal,
    setShowPromotionsModal,
    applyPromotionOnOrder,
    promotionSchemes,
    onApplyPromotion,
    onSkipPromotion,
    onCancelPromotion,
    openPromotionsModal,
    removeId,
    handleRemove: handleRemoveDetails,
    onRequestPayment,
    isRequestingPayment: sendPaymentRequestResponse.isLoading,
    showRequestPayment,
    advanceRemoveLoading,
  };
};
